import { API_BASE, getAccessToken } from "@/lib/api/session";

export type PracticeConsentMeta = {
  consentVersion: string;
  dpaVersion: string;
  summarySv: string[];
  defaultShareScope: Record<string, boolean>;
};

export type InvitePeek = {
  valid: boolean;
  practiceName: string | null;
  clinicianName: string | null;
  expiresAt: string;
  consentVersion: string;
};

export type ClientLink = {
  linkId: string;
  status: string;
  practiceName: string | null;
  clinicianName: string | null;
  consentVersion: string | null;
  consentAcceptedAt: string | null;
  shareScope: Record<string, boolean> | null;
  linkedAt: string;
};

export type HomeworkPayload = {
  linked: boolean;
  practiceName: string | null;
  linkId?: string;
  assignments: Array<{
    id: string;
    experimentId: string;
    title: string;
    status: string;
    dueAt: string | null;
    label: string | null;
    note: string | null;
    createdAt: string;
  }>;
};

export async function fetchConsentMeta(): Promise<PracticeConsentMeta | null> {
  try {
    const res = await fetch(`${API_BASE}/api/v1/practice/consent-meta`);
    if (!res.ok) return null;
    return (await res.json()) as PracticeConsentMeta;
  } catch {
    return null;
  }
}

export async function peekInvite(opts: {
  token?: string;
  code?: string;
}): Promise<InvitePeek | null> {
  try {
    const params = new URLSearchParams();
    if (opts.token) params.set("token", opts.token);
    if (opts.code) params.set("code", opts.code);
    const res = await fetch(
      `${API_BASE}/api/v1/practice/invites/peek?${params.toString()}`
    );
    if (!res.ok) return null;
    return (await res.json()) as InvitePeek;
  } catch {
    return null;
  }
}

export async function acceptPracticeInvite(opts: {
  token?: string;
  code?: string;
}): Promise<
  | { ok: true; practiceName: string; linkId: string }
  | { ok: false; message: string }
> {
  const token = await getAccessToken();
  if (!token) return { ok: false, message: "Logga in först." };
  try {
    const res = await fetch(`${API_BASE}/api/v1/practice/invites/accept`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(opts),
    });
    const json = (await res.json()) as {
      ok?: boolean;
      practiceName?: string;
      linkId?: string;
      message?: string;
      error?: string;
    };
    if (!res.ok || !json.ok) {
      return {
        ok: false,
        message: json.message ?? "Kunde inte acceptera inbjudan.",
      };
    }
    return {
      ok: true,
      practiceName: json.practiceName ?? "mottagning",
      linkId: json.linkId ?? "",
    };
  } catch {
    return { ok: false, message: "Nätverksfel." };
  }
}

export async function listClientLinks(): Promise<ClientLink[]> {
  const token = await getAccessToken();
  if (!token) return [];
  try {
    const res = await fetch(`${API_BASE}/api/v1/practice/client/links`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return [];
    const json = (await res.json()) as { links?: ClientLink[] };
    return json.links ?? [];
  } catch {
    return [];
  }
}

export async function fetchHomework(): Promise<HomeworkPayload> {
  const token = await getAccessToken();
  if (!token) return { linked: false, practiceName: null, assignments: [] };
  try {
    const res = await fetch(`${API_BASE}/api/v1/practice/client/homework`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return { linked: false, practiceName: null, assignments: [] };
    return (await res.json()) as HomeworkPayload;
  } catch {
    return { linked: false, practiceName: null, assignments: [] };
  }
}

export async function fetchClientMessages(): Promise<
  Array<{ id: string; senderRole: string; body: string; createdAt: string }>
> {
  const token = await getAccessToken();
  if (!token) return [];
  try {
    const res = await fetch(`${API_BASE}/api/v1/practice/client/messages`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return [];
    const json = (await res.json()) as {
      messages?: Array<{
        id: string;
        senderRole: string;
        body: string;
        createdAt: string;
      }>;
    };
    return json.messages ?? [];
  } catch {
    return [];
  }
}

export async function sendClientMessage(
  body: string
): Promise<{ ok: boolean; message?: string }> {
  const token = await getAccessToken();
  if (!token) return { ok: false, message: "Logga in." };
  try {
    const res = await fetch(`${API_BASE}/api/v1/practice/client/messages`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ body }),
    });
    if (!res.ok) {
      const json = (await res.json().catch(() => ({}))) as { message?: string };
      return { ok: false, message: json.message ?? "Kunde inte skicka." };
    }
    return { ok: true };
  } catch {
    return { ok: false, message: "Nätverksfel." };
  }
}

export async function disconnectClientLink(
  linkId: string
): Promise<{ ok: boolean; message?: string }> {
  const token = await getAccessToken();
  if (!token) return { ok: false, message: "Logga in." };
  try {
    const res = await fetch(
      `${API_BASE}/api/v1/practice/client/links/${linkId}/disconnect`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: "{}",
      }
    );
    if (!res.ok) {
      const json = (await res.json().catch(() => ({}))) as { message?: string };
      return { ok: false, message: json.message ?? "Kunde inte koppla ifrån." };
    }
    return { ok: true };
  } catch {
    return { ok: false, message: "Nätverksfel." };
  }
}
